import { useInfiniteQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { UserListProps, UserService } from '@services/UserService';

import { BackofficeUserQueryKeysEnum, ITEMS_LIMIT_PER_PAGE } from '@constants';
import { toastifyApiErrors } from '@utils';
import { useFilterParams, useAuth } from '@hooks';
import { loadParamsBasedOnKeys } from 'hooks/services/beneficiaries/list/utils';
import { FilterParamsEnum } from 'constants/private/global/filterParamsEnum';

export function useBackofficeUserMobileState() {
  const { searchParam, mappedFilters } = useFilterParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const userService = useMemo(() => new UserService(), []);
  const [searchParams] = useSearchParams();

  const backofficeCompanyId = user?.companyId ?? '';

  const mappedParams = loadParamsBasedOnKeys(searchParams, [
    FilterParamsEnum.STATUS,
    FilterParamsEnum.PROFILE_ID,
  ]);

  const backofficeUserMobileState = useInfiniteQuery<UserListProps>({
    queryKey: [
      BackofficeUserQueryKeysEnum.USERS_STATE,
      backofficeCompanyId,
      searchParam,
      mappedFilters,
      mappedParams,
    ],
    queryFn: ({ pageParam = 1 }) =>
      userService.getBackofficeAccounts({
        ...searchParam,
        ...mappedFilters,
        companyId: backofficeCompanyId,
        offset: ITEMS_LIMIT_PER_PAGE * (pageParam - 1),
        limit: ITEMS_LIMIT_PER_PAGE,
      }),
    enabled: Boolean(backofficeCompanyId),
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage =
        lastPage.total > allPages.length * ITEMS_LIMIT_PER_PAGE;

      return hasNextPage ? allPages.length + 1 : undefined;
    },
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshBackofficeUserList = () => {
    backofficeUserMobileState.remove();
    backofficeUserMobileState.refetch();
  };

  return {
    refreshBackofficeUserList,
    backofficeUserMobileState,
  };
}
